import React from 'react';
import { Users, MapPin, User, Heart, Target } from 'lucide-react';
import { SegmentStatsCard } from '@/components/v11/segments/SegmentStatsCard';
import { DistributionCard } from '@/components/v11/segments/DistributionCard';
import {
  mockSegmentStats,
  mockGenderDistribution,
  mockAgeDistribution,
  mockLocationDistribution,
  mockLifestyleDistribution,
  mockInterestsDistribution,
  mockSavedSegments,
} from '@/data/mockSegments';
import { Badge } from '@/components/ui/badge';

export const FeltonSegments: React.FC = () => {
  const selectedSegment = mockSavedSegments[0];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/20">
            <Users className="h-6 w-6 text-green-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-v11-text-primary">Segment Builder</h1>
            <p className="text-sm text-v11-text-muted">Build and analyze audience segments</p>
          </div>
        </div>
        <Badge variant="secondary" className="bg-v11-surface-hover text-v11-text-secondary">
          Demo with mock data
        </Badge>
      </div>

      {/* Current Segment Info */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-v11-text-primary">{selectedSegment.name}</h3>
            <p className="text-sm text-v11-text-secondary">{selectedSegment.description}</p>
          </div>
          <div className="text-right">
            <div className="text-sm text-v11-text-muted">{selectedSegment.filterCount} filters active</div>
            {selectedSegment.isDefault && (
              <Badge variant="secondary" className="mt-1">Default</Badge>
            )}
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <SegmentStatsCard
        totalSize={mockSegmentStats.totalSize}
        totalInteractions={mockSegmentStats.totalInteractions}
        avgEngagementRate={mockSegmentStats.avgEngagementRate}
      />

      {/* Distributions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <DistributionCard
          title="Gender Distribution"
          data={mockGenderDistribution}
          icon={<User className="h-4 w-4" />}
          colorClass="bg-blue-500"
        />

        <DistributionCard
          title="Age Distribution"
          data={mockAgeDistribution}
          icon={<Users className="h-4 w-4" />}
          colorClass="bg-purple-500"
        />

        <DistributionCard
          title="Location Distribution"
          data={mockLocationDistribution}
          icon={<MapPin className="h-4 w-4" />}
          colorClass="bg-green-500"
        />

        <DistributionCard
          title="Lifestyle Distribution"
          data={mockLifestyleDistribution}
          icon={<Heart className="h-4 w-4" />}
          colorClass="bg-pink-500"
        />

        <DistributionCard
          title="Top Interests"
          data={mockInterestsDistribution}
          icon={<Target className="h-4 w-4" />}
          colorClass="bg-orange-500"
        />
      </div>

      {/* Info Panel */}
      <div className="mt-6 p-4 bg-v11-bg-secondary rounded-lg border border-v11-border">
        <p className="text-xs text-v11-text-muted">
          <strong>Segment Builder Features (Mock Data):</strong>
          <br />• View segment statistics and distributions
          <br />• Analyze audience demographics and psychographics
          <br />• Multiple saved segments available
          <br />• Real filtering and API integration coming soon
        </p>
      </div>
    </div>
  );
};
